import { Action } from "redux";
import { ThunkDispatch } from 'redux-thunk';
import { connect } from 'react-redux';
import { getSchoolFilterOptions, getSelectedSchools, SchoolOption, getSchoolGroups, getCentreGroupsByCountry } from 'app/pages/DiscountEditor/modules/ProductSelection';
import { Checkbox } from 'antd';
import { RightOutlined, DownOutlined } from '@ant-design/icons';
import { changeSchoolSelectionCommand, changeSchoolGroupSelectionCommand, changeCentreGroupSelectionCommand } from 'app/pages/DiscountEditor/commands';
import ComponentHeader from 'app/shared/ui/ComponentHeader';
import { SelectionMap } from 'app/shared/model/ProductFilter';
import ScrollPanel from 'app/shared/ui/ScrollPanel';
import { SchoolSelectionGroup, CentreSelectionGroup } from 'app/pages/DiscountEditor/modules/ProductSelection/state';
import { changeSchoolGroupOpenState, changeCentreGroupOpenState } from 'app/pages/DiscountEditor/actions';
import ErrorSummary from 'app/pages/DiscountEditor/components/ErrorSummary';
import { getSectionError } from 'app/pages/DiscountEditor/modules/Editor';
import { SaveDiscountErrorCategory } from 'app/shared/model/SaveDiscountResultDto';

type SchoolEditorInteralProps = SchoolEditorStateProps & SchoolEditorDispatchProps;

interface SchoolEditorStateProps {
    errorMessage?: string
    schools: SchoolOption[]
    selectedSchools: SelectionMap
    schoolGroups: SchoolSelectionGroup[]
    centreGroupsByCountry: { [countryCode: string]: CentreSelectionGroup[] }
}

interface SchoolEditorDispatchProps {
    changeSchoolSelection: (code: string, isSelected: boolean) => void
    changeSchoolGroupOpenState: (code: string, isOpen: boolean) => void
    changeSchoolGroupSelection: (code: string, isSelected: boolean) => void
    changeCentreGroupOpenState: (centreCode: string, isOpen: boolean) => void
    changeCentreGroupSelection: (centreCode: string, isSelected: boolean) => void
}

interface SchoolChecklistProps {
    schools: SchoolOption[]
    selectedSchools: SelectionMap
    changeSchoolSelection: (code: string, isSelected: boolean) => void
}

const SchoolChecklist = (props: SchoolChecklistProps) => (
    <div style={{ marginLeft: 39, marginTop: 4 }}>
        {props.schools.map(s =>
            <div key={s.code} style={{ marginBottom: 4 }}>
                <Checkbox
                    checked={!!props.selectedSchools[s.code]}
                    onChange={() => props.changeSchoolSelection(s.code, !props.selectedSchools[s.code])}>
                    {s.name}
                </Checkbox>
            </div>
        )}
    </div>
)

const SchoolSelectionEditor = (props: SchoolEditorInteralProps) => (
    <div style={{ marginBottom: 16, background: '#fff', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
        <ComponentHeader text="Schools" />
        <div style={{ padding: '0 16px 16px' }}>
            <ErrorSummary errorMessage={props.errorMessage} />
            <ScrollPanel>
                <div style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {props.schoolGroups.map(g => {
                        const centreGroups = props.centreGroupsByCountry[g.groupCode] ?? [];
                        return (
                            <div key={g.groupCode} style={{ marginBottom: 4 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span
                                        onClick={() => props.changeSchoolGroupOpenState(g.groupCode, !g.isOpen)}
                                        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', width: 16 }}
                                    >
                                        {g.isOpen ? <DownOutlined style={{ fontSize: 12 }} /> : <RightOutlined style={{ fontSize: 12 }} />}
                                    </span>
                                    <Checkbox
                                        checked={g.itemsSelected > 0 && g.itemsNotSelected === 0}
                                        onChange={() => props.changeSchoolGroupSelection(g.groupCode, g.itemsNotSelected != 0)}
                                        indeterminate={g.itemsSelected > 0 && g.itemsNotSelected > 0}>
                                        {g.title}
                                    </Checkbox>
                                </div>
                                {g.isOpen && centreGroups.map(cg => (
                                    <div key={cg.centreCode} style={{ marginLeft: 24, marginTop: 4 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span
                                                onClick={() => props.changeCentreGroupOpenState(cg.centreCode, !cg.isOpen)}
                                                style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', width: 16 }}
                                            >
                                                {cg.isOpen ? <DownOutlined style={{ fontSize: 12 }} /> : <RightOutlined style={{ fontSize: 12 }} />}
                                            </span>
                                            <Checkbox
                                                checked={cg.itemsSelected > 0 && cg.itemsNotSelected === 0}
                                                onChange={() => props.changeCentreGroupSelection(cg.centreCode, cg.itemsNotSelected != 0)}
                                                indeterminate={cg.itemsSelected > 0 && cg.itemsNotSelected > 0}>
                                                {cg.title}
                                            </Checkbox>
                                        </div>
                                        {cg.isOpen && (
                                            <SchoolChecklist
                                                schools={props.schools.filter(s => s.school.centreCode === cg.centreCode)}
                                                selectedSchools={props.selectedSchools}
                                                changeSchoolSelection={props.changeSchoolSelection}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </ScrollPanel>
        </div>
    </div>
)

function mapStateToProps(state: any, ownProps: {}) : SchoolEditorStateProps {
    return {
        schoolGroups: getSchoolGroups(state),
        schools: getSchoolFilterOptions(state),
        selectedSchools: getSelectedSchools(state),
        centreGroupsByCountry: getCentreGroupsByCountry(state),
        errorMessage: getSectionError(state, SaveDiscountErrorCategory.Schools)
    }
}

function mapDispatchToProps(dispatch: ThunkDispatch<any, any, Action>) : SchoolEditorDispatchProps {
    return {
        changeSchoolSelection: (code: string, isSelected: boolean) => {
            dispatch(changeSchoolSelectionCommand(isSelected, code));
        },

        changeSchoolGroupOpenState: (code: string, isOpen: boolean) => {
            dispatch(changeSchoolGroupOpenState(isOpen, code));
        },

        changeSchoolGroupSelection: (code: string, isSelected: boolean) => {
            dispatch(changeSchoolGroupSelectionCommand(isSelected, code));
        },

        changeCentreGroupOpenState: (centreCode: string, isOpen: boolean) => {
            dispatch(changeCentreGroupOpenState(isOpen, centreCode));
        },

        changeCentreGroupSelection: (centreCode: string, isSelected: boolean) => {
            dispatch(changeCentreGroupSelectionCommand(isSelected, centreCode));
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(SchoolSelectionEditor);